// Developer: Ankit Gupta
import { useState } from 'react';
import { Badge, Button, Empty, List, Popover, Space, Tag, Typography } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationList,
  useUnreadCount,
  type NotificationItem,
} from '@/auth/useNotifications';
import { ShimmerBox } from './Skeletons';
import i18n, { useT } from '@/i18n';
import { useIsRtl } from '@/hooks/useDirection';
import { browserOrUiLocale } from '@/utils/format';

const { Text } = Typography;

const LEVEL_COLOR: Record<string, string> = {
  info: 'blue',
  success: 'green',
  warning: 'orange',
  error: 'red',
};

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return i18n.t('common:time.justNow');
  const mins = Math.floor(secs / 60);
  if (mins < 60) return i18n.t('common:time.minutesAgo', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return i18n.t('common:time.hoursAgo', { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 30) return i18n.t('common:time.daysAgo', { count: days });
  return new Date(iso).toLocaleDateString(browserOrUiLocale());
}

/** Something waiting for the person elsewhere in the product (an Orion step to allow, a patch to
 *  review): shown above the notifications and counted in the badge. */
export interface WaitingItem { key: string; title: string; body: string; link: string }

export default function NotificationBell({ waiting = [] }: { waiting?: WaitingItem[] }) {
  const { t } = useT('shell');
  const rtl = useIsRtl();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: unread } = useUnreadCount();
  const { data: items = [], isLoading } = useNotificationList(open);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const handleClick = (n: NotificationItem) => {
    if (!n.is_read) markRead.mutate(n.id);
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  };

  const content = (
    <div style={{ width: 360, maxHeight: 440, overflow: 'auto' }}>
      {waiting.length > 0 && (
        <div className="dp-bell-waiting">
          <div className="dp-bell-section">{t('notifications.waiting', { count: waiting.length })}</div>
          {waiting.map((w) => (
            <button key={w.key} type="button" className="dp-bell-item" onClick={() => { navigate(w.link); setOpen(false); }}>
              <span className="dp-bell-item-top"><span className="dp-bell-dot" aria-hidden="true" /><strong dir="auto">{w.title}</strong></span>
              <span className="dp-bell-item-body" dir="auto">{w.body}</span>
            </button>
          ))}
          <div className="dp-bell-section">{t('notifications.title')}</div>
        </div>
      )}
      {isLoading ? (
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <ShimmerBox width={45} height={18} borderRadius={4} />
                <ShimmerBox width="65%" height={18} />
              </div>
              <ShimmerBox width="90%" height={14} />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('notifications.empty')} />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(n) => (
            <List.Item
              style={{
                cursor: n.link ? 'pointer' : 'default',
                background: n.is_read ? 'transparent' : 'var(--dp-nav-hover)',
                padding: '8px 12px',
                alignItems: 'flex-start',
              }}
              onClick={() => handleClick(n)}
            >
              <List.Item.Meta
                title={
                  <Space size={6}>
                    <Tag color={LEVEL_COLOR[n.level] ?? 'blue'} style={{ marginInlineEnd: 0 }}>
                      {t(`notifications.level.${n.level}`, { defaultValue: n.level })}
                    </Tag>
                    <Text strong={!n.is_read}>{n.title}</Text>
                  </Space>
                }
                description={
                  <div>
                    {n.body && <div style={{ fontSize: 12 }}>{n.body}</div>}
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {timeAgo(n.created_at)}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  const title = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Text strong>{t('notifications.title')}</Text>
      <Button
        type="link"
        size="small"
        icon={<CheckOutlined />}
        disabled={!unread?.unread}
        onClick={() => markAll.mutate()}
      >
        {t('notifications.markAllRead')}
      </Button>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement={rtl ? 'bottomLeft' : 'bottomRight'}
      title={title}
      content={content}
    >
      <Badge count={(unread?.unread ?? 0) + waiting.length} size="small" overflowCount={99}>
        <Button className="dp-bell" aria-label={t('notifications.title')} icon={<BellOutlined style={{ fontSize: 16 }} />} />
      </Badge>
    </Popover>
  );
}
